// PLATFORM-UX-001 — Detección de versión nueva del frontend (singleton).
// Mecanismo: comparar el hash del bundle CARGADO (script main.<hash>.js)
// contra /asset-manifest.json (regenerado por CRA en CADA build, servido
// estático — cero cambios backend/nginx). Poll cada 5 min + window focus +
// visibilitychange. Estados: current → new → (confirmado) deferred → reload.
// El reload es un location.reload() simple: la preservación (sesión en
// localStorage + re-auth por deviceLogin, contexto UX-001D en sessionStorage,
// ruta en URL, socket reconecta) ya está garantizada por construcción.
import { busyRegistry } from './busyRegistry';

export type VersionStatus = 'current' | 'new' | 'deferred';
export interface VersionState {
  status: VersionStatus;
  newHash: string | null;
}

const POLL_MS = 5 * 60 * 1000;      // detección silenciosa: cada 5 min (build B — validación Platform-UX-001)
const FIRST_CHECK_MS = 12 * 1000;   // primer chequeo tras el boot
const DISMISS_KEY = 'kf25_ver_dismissed';

const loadedHash: string | null = (() => {
  try {
    const src = Array.from(document.querySelectorAll('script[src*="/static/js/"]'))
      .map(s => (s.getAttribute('src') || ''))
      .find(s => /main\.[a-f0-9]+\.js/.test(s)) || '';
    const m = src.match(/main\.([a-f0-9]+)\.js/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
})();

let state: VersionState = { status: 'current', newHash: null };
let dismissedHash: string | null = null;
try { dismissedHash = sessionStorage.getItem(DISMISS_KEY); } catch { /* noop */ }

const listeners = new Set<() => void>();
function setState(s: VersionState) {
  state = s;
  listeners.forEach(fn => fn());
}
export function subscribeVersion(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
export function getVersionState(): VersionState { return state; }

async function check() {
  // Sin hash cargado (patrón de script inesperado) → detección deshabilitada.
  if (!loadedHash) return;
  try {
    const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
    if (!res.ok) return;
    const manifest = await res.json();
    const entry: unknown = manifest?.files?.['main.js'];
    const m = typeof entry === 'string' ? entry.match(/main\.([a-f0-9]+)\.js/) : null;
    const remoteHash = m ? m[1] : null;
    if (!remoteHash) return;

    if (remoteHash === loadedHash) {
      if (state.status !== 'current') setState({ status: 'current', newHash: null });
      return;
    }
    // Versión distinta: ¿descartada para esta versión? → silencio.
    if (remoteHash === dismissedHash) return;
    // ¿Ya confirmada (deferred) para esta misma versión? → no re-notificar.
    if (state.status === 'deferred' && state.newHash === remoteHash) return;
    setState({ status: 'new', newHash: remoteHash });
  } catch {
    // offline / red — reintentar en el próximo poll (silencioso)
  }
}

export function dismissVersion() {
  if (state.newHash) {
    try { sessionStorage.setItem(DISMISS_KEY, state.newHash); } catch { /* noop */ }
    dismissedHash = state.newHash;
  }
  setState({ status: 'current', newHash: null }); // también cancela un deferred
}

export function confirmUpdate() {
  if (!state.newHash) return;
  if (busyRegistry.isBusy()) {
    // Operación crítica en curso → diferir: el banner muestra "pendiente" y el
    // refresh se aplica automáticamente al quedar idle (suscripción abajo).
    setState({ status: 'deferred', newHash: state.newHash });
    return;
  }
  applyRefresh();
}

function applyRefresh() {
  try { sessionStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
  window.location.reload();
}

// busy → idle con actualización diferida → aplicar ahora.
busyRegistry.subscribe(() => {
  if (state.status === 'deferred' && !busyRegistry.isBusy()) applyRefresh();
});

let initialized = false;
export function initVersionAwareness() {
  // Marca para el script inline de index.html (bootstrap): el bundle moderno
  // gestiona la detección → el inline se abstiene (sin doble banner).
  (window as any).__kf25_versionAware = true;
  if (initialized) return;
  initialized = true;
  if (!loadedHash) return;
  window.setTimeout(check, FIRST_CHECK_MS);
  window.setInterval(check, POLL_MS);
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
}
